import { usePatientContext } from '@/components/Contexts/PatientContext';

import SvgChart, { SVGRenderer } from '@wuba/react-native-echarts/svgChart';
import * as echarts from 'echarts/core';
import { useRef, useEffect, useState } from 'react';
import { BarChart, LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  MarkAreaComponent,
  LegendComponent,
  MarkLineComponent,
} from 'echarts/components';
import DateUtils from '@/utils/DateUtils';
import { LayoutChangeEvent, View } from 'react-native';
import { SubPatientSubscription, useGetImplantsQuery } from '@/data/data';
import visionData from '@/data/visionData';
import { Color } from '@/GlobalStyles';
import SimpleInterpolation from '@/utils/SimpleInterpolation';
import { useTranslation } from 'react-i18next';

// Register extensions
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  SVGRenderer,
  BarChart,
  LineChart,
  MarkAreaComponent,
  LegendComponent,
  MarkLineComponent,
]);

const E_HEIGHT = 400;
const E_WIDTH = 300;

type DefocusChartProps = {
  data?: [number, number][];
};

const DefocusChart = (props: DefocusChartProps) => {
  const { data } = props;

  const chartRef = useRef<any>(null);
  const svgRef = useRef<any>(null);
  const [width, setWidth] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.resize({
        width: width,
        height: E_HEIGHT,
      });
    }
  }, [width]);

  useEffect(() => {
    let chart: any;
    if (svgRef.current) {
      const option = {
        legend: {
          type: 'plain',
          bottom: 0,
        },
        tooltip: {
          trigger: 'axis',
          formatter: (value, index) => {
            let hadPatient = false;
            const tooltip = value
              .map((element) => {
                if (element.seriesName === 'Patient' && hadPatient) {
                  return null;
                }
                if (element.seriesName === 'Patient') {
                  hadPatient = true;
                }
                const unit = element.seriesName !== 'Patient' ? ' AV(LogMAR)' : '%';
                return `${element.seriesName} : ${element.data[0]} δ -> ${element.data[1]}${unit} `;
              })
              .filter((e) => e !== null);

            return `${tooltip.join('<br/>')}`;
          },
          position: [0, 0],
        },
        grid: {
          left: '10%',
          right: '10%',
          bottom: '20%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLine: { onZero: false },
          max: 2,
          min: -4,
          inverse: true,
          axisLabel: {
            formatter: (value, index) => {
              return `${value} δ`;
            },
          },
          interval: width < 600 ? 1 : 0.5,
        },
        yAxis: [
          {
            name: 'AV(LogMAR)',
            nameLocation: 'middle',
            nameTextStyle: {
              padding: [0, 0, 17, 0],
            },
            min: -0.2,
            max: 0.8,
            type: 'value',
            axisLine: { onZero: false },
            inverse: true,
            splitLine: {
              show: false,
            },
            axisLabel: {
              formatter: (value) => {
                if (value >= -0.3) {
                  return value;
                } else {
                  return '';
                }
              },
            },
          },
        ],
        series: [
          {
            name: t('Courbe de défocus'),
            unit: '',
            data: data.filter((visionData) => visionData[1] !== null),
            id: 'implant',
            type: 'line',
            yAxisIndex: 0,
            smooth: true,
            color: Color.MainBleu,
            symbolSize: 5,
            markLine: {
              data: [
                { name: 'Vision', xAxis: -4, yAxis: 0 },
                { xAxis: 2, yAxis: 0 },
                { name: 'Vision', xAxis: -4, yAxis: 0.2 },
                { xAxis: 2, yAxis: 0.2 },
              ],
              dashOffset: 0,
              symbol: ['none', 'none'],
              silent: true,
              label: {
                color: 'transparent',
              },
            },
          },
        ],
      };

      // @ts-ignore
      if (!chartRef.current) {
        chart = echarts.init(svgRef.current, 'light', {
          renderer: 'svg',
          width: width,
          height: E_HEIGHT,
        });
        chartRef.current = chart;
      }

      chartRef.current.setOption(option);
    }
  }, [data, width]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  const handleLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setWidth(width);
  };

  return (
    <View onLayout={handleLayout} style={{ flex: 1, flexShrink: 0, flexBasis: 'auto' }}>
      <SvgChart ref={svgRef} />
    </View>
  );
};

export default DefocusChart;
